/**
 * Requests – Mobile Portal
 *
 * Purpose: Submit equipment/trip/agent-visit requests; list my requests. Ketchup portal approves/rejects.
 * Location: mobile-portal/src/pages/Requests.tsx
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fieldRequestsAPI, mobileUnitsAPI, agentAPI } from '@smartpay/api-client/ketchup';
import type { FieldRequest, FieldRequestType, AgentVisitReason, MobileUnit } from '@smartpay/api-client/ketchup';
import {
  Button,
  Card,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  useToast,
} from '@smartpay/ui';
import { Send, Package } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const AGENT_VISIT_REASONS: { value: AgentVisitReason; label: string }[] = [
  { value: 'top_up', label: 'Low liquidity / Top-up' },
  { value: 'pos_servicing', label: 'POS servicing' },
  { value: 'pos_replacement', label: 'POS replacement' },
  { value: 'training', label: 'Training' },
  { value: 'support', label: 'Support' },
];

export default function Requests() {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<{
    type: FieldRequestType;
    mobileUnitId: '' | string;
    agentId: string;
    requestReason: AgentVisitReason | '';
    payload: string;
  }>({ type: 'equipment', mobileUnitId: '', agentId: '', requestReason: '', payload: '{}' });
  const [payloadError, setPayloadError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  const { data: unitsResponse } = useQuery({
    queryKey: ['mobile-units-for-requests'],
    queryFn: () => mobileUnitsAPI.getAll({}),
  });
  const rawUnits = unitsResponse as { data?: MobileUnit[] } | undefined;
  const unitsList = Array.isArray(rawUnits) ? rawUnits : (rawUnits?.data ?? []);
  const defaultUnitId = (user as { mobileUnitId?: string })?.mobileUnitId ?? unitsList[0]?.id ?? '';
  const mobileUnitIdFilter = defaultUnitId || undefined;

  const { data: agentsResponse } = useQuery({
    queryKey: ['agents-for-agent-visit'],
    queryFn: () => agentAPI.getAll({ limit: 500 }),
  });
  const agentsList = (() => {
    const raw = agentsResponse as { data?: { id: string; name: string; type?: string }[] } | undefined;
    const list = raw?.data ?? [];
    return list.filter((a) => (a as { type?: string }).type !== 'mobile_unit');
  })();

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['field-requests', mobileUnitIdFilter],
    queryFn: () => fieldRequestsAPI.list({ mobileUnitId: mobileUnitIdFilter }),
  });

  const createMutation = useMutation({
    mutationFn: (body: {
      type: FieldRequestType;
      mobileUnitId: string;
      agentId?: string;
      requestReason?: string;
      payload?: Record<string, unknown>;
    }) => fieldRequestsAPI.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-requests'] });
      setShowForm(false);
      setForm({ type: 'equipment', mobileUnitId: defaultUnitId, agentId: '', requestReason: '', payload: '{}' });
    },
    onError: (e) => toast({ title: 'Request failed', description: e instanceof Error ? e.message : 'Please try again.', variant: 'destructive' }),
  });

  const handleSubmit = () => {
    setPayloadError(null);
    const unitId = form.mobileUnitId || defaultUnitId;
    if (!unitId) return;
    if (form.type === 'agent_visit' && !form.agentId) return;
    let payload: Record<string, unknown> = {};
    if (form.payload.trim()) {
      try {
        payload = JSON.parse(form.payload);
      } catch {
        setPayloadError('Details must be valid JSON (e.g. {"item": "tablet"}).');
        return;
      }
    }
    createMutation.mutate({
      type: form.type,
      mobileUnitId: unitId,
      payload,
      ...(form.type === 'agent_visit' && {
        agentId: form.agentId,
        requestReason: form.requestReason || undefined,
      }),
    });
  };

  const statusBadge = (status: string) => {
    const cls = status === 'approved' ? 'bg-green-100 text-green-800' : status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800';
    return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{status}</span>;
  };

  const typeLabel = (type: string) =>
    type === 'equipment' ? 'Equipment' : type === 'trip' ? 'Trip' : type === 'agent_visit' ? 'Agent visit' : type;

  const reasonLabel = (reason: string | null) =>
    AGENT_VISIT_REASONS.find((r) => r.value === reason)?.label ?? reason ?? '—';
  const agentName = (id: string | null) => (id ? agentsList.find((a) => a.id === id)?.name ?? id : '—');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Requests</h1>
          <p className="text-muted-foreground">Equipment & trip requests – Ketchup admins approve in Ketchup Portal</p>
        </div>
        <Button onClick={() => { setShowForm(true); setForm({ type: 'equipment', mobileUnitId: defaultUnitId, agentId: '', requestReason: '', payload: '{}' }); }}>
          <Send className="h-4 w-4 mr-2" /> New request
        </Button>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Submit request</DialogTitle>
            <DialogDescription>
              Request equipment, a trip, or an agent visit (support, top-up, POS, training). Ketchup operations will review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <label className="block font-medium">Type</label>
            <select
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as FieldRequestType }))}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="equipment">Equipment</option>
              <option value="trip">Trip</option>
              <option value="agent_visit">Agent visit</option>
              <option value="other">Other</option>
            </select>
            {form.type === 'agent_visit' && (
              <>
                <label className="block font-medium">Agent to attend <span className="text-destructive">*</span></label>
                <select
                  value={form.agentId}
                  onChange={(e) => setForm((f) => ({ ...f, agentId: e.target.value }))}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">Select agent</option>
                  {agentsList.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
                <label className="block font-medium">Reason</label>
                <select
                  value={form.requestReason}
                  onChange={(e) => setForm((f) => ({ ...f, requestReason: e.target.value as AgentVisitReason }))}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">Select reason</option>
                  {AGENT_VISIT_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </>
            )}
            <label className="block font-medium">Mobile unit</label>
            <select
              value={form.mobileUnitId || defaultUnitId}
              onChange={(e) => setForm((f) => ({ ...f, mobileUnitId: e.target.value }))}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Select unit</option>
              {unitsList.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
            <label className="block font-medium">Details (JSON, optional)</label>
            <Input
              placeholder='{"item": "tablet", "reason": "replacement"}'
              value={form.payload}
              onChange={(e) => { setForm((f) => ({ ...f, payload: e.target.value })); setPayloadError(null); }}
              className="font-mono text-xs"
            />
            {payloadError && (
              <p className="text-sm text-destructive" role="alert">{payloadError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button
              disabled={
                !(form.mobileUnitId || defaultUnitId) ||
                (form.type === 'agent_visit' && !form.agentId) ||
                createMutation.isPending
              }
              onClick={handleSubmit}
            >
              {createMutation.isPending ? 'Submitting…' : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <div className="p-0">
          {isLoading ? (
            <div className="p-6 text-center text-muted-foreground">Loading requests...</div>
          ) : requests.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
              <p>No requests yet. Submit equipment or trip requests; Ketchup will approve in Ketchup Portal.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Agent / Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Reviewed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(requests as FieldRequest[]).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{typeLabel(r.type)}</TableCell>
                    <TableCell>{r.mobileUnitId}</TableCell>
                    <TableCell>
                      {r.type === 'agent_visit'
                        ? `${agentName(r.agentId ?? null)} · ${reasonLabel(r.requestReason ?? null)}`
                        : '—'}
                    </TableCell>
                    <TableCell>{statusBadge(r.status)}</TableCell>
                    <TableCell>{new Date(r.createdAt).toLocaleString()}</TableCell>
                    <TableCell>{r.reviewedAt ? new Date(r.reviewedAt).toLocaleString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>
    </div>
  );
}
